import {
  useCallback,
} from 'react';
import { upperFirst } from 'lodash';
import {
  useSnackbar,
  useTable,
  useTableFetch,
  useTranslations,
  useTableLocalization,
  useTablePreserverActivePageValidator,
  useTablePreserverDecoder,
  useTablePreserverEncoder,
  usePubSub,
  buildTablePreserverDecoderConfigItem,
  IUseTableReturn,
  IUseTableResetTableMethodsReturn,
  IUseTableFetchReturn,
  IUseTableLocalizationReturn,
  TUseTablePreserverDecoderConfigs,
  TablePreserverDecoderConfigItemTypeEnum,
  TSetSearchParams,
} from '@uniqkey-frontend/shared-app';
import {
  OrganizationVerifiedDomainsProperty,
  GetVerifiedDomainsResponseModel,
} from '@uniqkey-backend-partner/api-client';
import { useSearchParams } from 'react-router-dom';
import PubSubEventEnum from '../../../enums/PubSubEventEnum';
import usePartnerOrganizationsAPI from '../../usePartnerOrganizationsAPI';
import {
  IGetOrganizationVerifiedDomainsParams,
  TGetOrganizationVerifiedDomainsMethod,
} from '../../usePartnerOrganizationsAPI/interfaces';
import {
  IOrganizationVerifiedDomainsTableRow,
} from '../../../components/tables/OrganizationVerifiedDomainsTable';

interface IPersistentFilters extends Pick<
  IGetOrganizationVerifiedDomainsParams, 'organizationId'
> {}

export interface IUseOrganizationVerifiedDomainsTableProps {
  persistentFilters: IPersistentFilters;
  noDataMessageKey: string;
}

export interface IUseOrganizationVerifiedDomainsTableReturn extends
  Omit<IUseTableReturn<IOrganizationVerifiedDomainsTableRow>, 'selectedRows' | 'resetTableMethods'>,
  Pick<IUseTableResetTableMethodsReturn, 'resetActivePage'>,
  Omit<IUseTableFetchReturn<GetVerifiedDomainsResponseModel>, 'data'>
{
  organizationVerifiedDomains: IUseTableFetchReturn<
    GetVerifiedDomainsResponseModel
  >['data'];
  selectedOrganizationVerifiedDomains: IUseTableReturn<
    IOrganizationVerifiedDomainsTableRow
  >['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
}

interface ITablePreservationConfigs extends
  Omit<IGetOrganizationVerifiedDomainsParams,
    'page' | 'pageLength' | 'organizationId' | 'orderPropertyName' | 'isDescending' | 'cancelToken'
  >,
  Pick<IUseTableReturn<
    IOrganizationVerifiedDomainsTableRow
  >, 'columnOrderBy' | 'columnOrderDirection'>
{
  activePage: IGetOrganizationVerifiedDomainsParams['page'];
  perPage: IGetOrganizationVerifiedDomainsParams['pageLength'];
}

const PRESERVATION_CONFIGS: TUseTablePreserverDecoderConfigs<ITablePreservationConfigs> = {
  activePage: true,
  perPage: true,
  columnOrderBy: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.ENUM,
    { enumToCheck: OrganizationVerifiedDomainsProperty },
  ),
  columnOrderDirection: true,
};

const useOrganizationVerifiedDomainsTable = (
  props: IUseOrganizationVerifiedDomainsTableProps,
): IUseOrganizationVerifiedDomainsTableReturn => {
  const {
    persistentFilters,
    noDataMessageKey,
  } = props;

  const { t } = useTranslations();
  const { showError } = useSnackbar();
  const { getOrganizationVerifiedDomains } = usePartnerOrganizationsAPI();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOnRequestError = useCallback(() => {
    showError({ text: t('common.somethingWentWrong') });
  }, [showError, t]);

  const {
    activePage: initialActivePage,
    perPage: initialPerPage,
    columnOrderBy: initialColumnOrderBy,
    columnOrderDirection: initialColumnOrderDirection,
  } = useTablePreserverDecoder<ITablePreservationConfigs>(searchParams, PRESERVATION_CONFIGS);

  const {
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedRows: selectedOrganizationVerifiedDomains,
    resetTableMethods,
    ...restTableProps
  } = useTable<IOrganizationVerifiedDomainsTableRow>({
    initialActivePage,
    initialPerPage,
    initialColumnOrderBy,
    initialColumnOrderDirection,
  });

  const {
    resetTable,
    resetActivePage,
  } = resetTableMethods;

  const handleTableReset = useCallback(() => {
    resetTable();
  }, [resetTable]);
  usePubSub(PubSubEventEnum.RESET_TABLE, handleTableReset);

  const {
    data: organizationVerifiedDomains, isLoading, isFetchedAfterMount, total, refetch,
  } = useTableFetch<
    IGetOrganizationVerifiedDomainsParams,
    TGetOrganizationVerifiedDomainsMethod,
    GetVerifiedDomainsResponseModel
  >({
    request: getOrganizationVerifiedDomains,
    params: {
      page: activePage,
      pageLength: perPage,
      orderPropertyName: OrganizationVerifiedDomainsProperty[
        upperFirst(columnOrderBy) as keyof typeof OrganizationVerifiedDomainsProperty
      ],
      isDescending: columnOrderDirection ? columnOrderDirection === 'desc' : undefined,
      ...persistentFilters,
    },
    onRequestError: handleOnRequestError,
    autoRefetch: true,
  });

  const { localization } = useTableLocalization({
    noDataMessageKey,
    isLoading,
  });

  useTablePreserverActivePageValidator({
    activePage, resetActivePage, perPage, total, isFetchedAfterMount,
  });

  useTablePreserverEncoder<ITablePreservationConfigs>(setSearchParams as TSetSearchParams, {
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
  });

  return {
    organizationVerifiedDomains,
    isLoading,
    total,
    refetch,
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedOrganizationVerifiedDomains,
    localization,
    resetActivePage,
    ...restTableProps,
  };
};

export default useOrganizationVerifiedDomainsTable;
